import { theme, type ThemeConfig } from 'antd';
export type ColorMode = 'light' | 'dark';
const LIGHT = {
  primary: '#1677FF',
  headerBg: '#FFFFFF',
  bodyBg: '#EEF4FB',
  containerBg: '#FFFFFF',
  elevatedBg: '#FFFFFF',
  borderSecondary: 'rgba(5, 5, 5, 0.06)',
  menuText: '#33445C',
  menuHoverBg: '#E8F1FE',
  menuHoverText: '#1677FF',
  menuSelectedBg: '#DCEAFF',
  menuSelectedText: '#0E5FD8',
  menuGroupTitle: 'rgba(0, 0, 0, 0.45)',
  tableHeaderBg: '#F3F8FF',
  tableHeaderText: 'rgba(0, 0, 0, 0.78)',
  tableRowHoverBg: '#EAF3FF',
  tableRowSelectedBg: '#DEEBFF',
  tableBorder: '#E1E9F5',
} as const;
const DARK: Record<keyof typeof LIGHT, string> = {
  primary: '#3D8BFF',
  headerBg: '#131F33',
  bodyBg: '#0A121F',
  containerBg: '#121F35',
  elevatedBg: '#17273F',
  borderSecondary: '#2A3F60',
  menuText: 'rgba(255, 255, 255, 0.78)',
  menuHoverBg: 'rgba(255, 255, 255, 0.08)',
  menuHoverText: '#FFFFFF',
  menuSelectedBg: '#1B3E6F',
  menuSelectedText: '#FFFFFF',
  menuGroupTitle: 'rgba(255, 255, 255, 0.42)',
  tableHeaderBg: '#16223A',
  tableHeaderText: 'rgba(255, 255, 255, 0.85)',
  tableRowHoverBg: '#16233A',
  tableRowSelectedBg: '#183357',
  tableBorder: '#1E2C42',
};
export function buildThemeConfig(mode: ColorMode): ThemeConfig {
  const c = mode === 'dark' ? DARK : LIGHT;
  return {
    algorithm: mode === 'dark' ? theme.darkAlgorithm : theme.defaultAlgorithm,
    token: {
      colorPrimary: c.primary,
      colorBgContainer: c.containerBg,
      colorBgElevated: c.elevatedBg,
      colorBorderSecondary: c.borderSecondary,
      borderRadius: 8,
      fontSize: 14,
      fontFamily:
        "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', sans-serif",
      /**
       * 机器码字体（钱包码 / 订单号 / CDKey 这类）。
       *
       * JetBrains Mono 的**零字形自带斜杠** ⇒ `O` 与 `0` 一眼可分；
       * 系统等宽字体（Menlo / Consolas / SF Mono）的零是光秃秃的椭圆。
       * 字体本体随应用分发（注册见 `app/codeFont.ts`），不指望用户机器上装了什么。
       */
      fontFamilyCode:
        "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, 'Liberation Mono', monospace",
    },
    components: {
      Layout: {
        headerBg: c.headerBg,
        bodyBg: c.bodyBg,
        headerHeight: 56,
        headerPadding: '0 16px',
      },
      Menu: {
        itemBg: 'transparent',
        subMenuItemBg: 'transparent',
        itemColor: c.menuText,
        itemHoverBg: c.menuHoverBg,
        itemHoverColor: c.menuHoverText,
        itemSelectedBg: c.menuSelectedBg,
        itemSelectedColor: c.menuSelectedText,
        itemActiveBg: c.menuSelectedBg,
        horizontalItemHoverColor: c.menuHoverText,
        horizontalItemSelectedColor: c.menuSelectedText,
        groupTitleColor: c.menuGroupTitle,
        itemBorderRadius: 6,
        itemHeight: 38,
        darkItemBg: 'transparent',
        darkSubMenuItemBg: 'transparent',
        darkItemColor: c.menuText,
        darkItemHoverBg: c.menuHoverBg,
        darkItemHoverColor: c.menuHoverText,
        darkItemSelectedBg: c.menuSelectedBg,
        darkItemSelectedColor: c.menuSelectedText,
        darkGroupTitleColor: c.menuGroupTitle,
        darkPopupBg: c.headerBg,
      },
      Table: {
        headerBg: c.tableHeaderBg,
        headerColor: c.tableHeaderText,
        rowHoverBg: c.tableRowHoverBg,
        rowSelectedBg: c.tableRowSelectedBg,
        borderColor: c.tableBorder,
        cellPaddingBlockSM: 8,
        cellPaddingInlineSM: 12,
        headerBorderRadius: 6,
      },
      Card: {
        bodyPadding: 16,
        bodyPaddingSM: 12,
        headerFontSizeSM: 13,
      },
      Typography: {
        titleMarginTop: 0,
        titleMarginBottom: 0,
      },
      Badge: {
        indicatorHeightSM: 14,
        textFontSizeSM: 10,
        indicatorHeight: 16,
        textFontSize: 11,
        textFontWeight: 500,
        paddingInline: 4,
        statusSize: 6,
      },
    },
  };
}
